import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

const COLLAPSED_KEY = 'atlas-sidebar-collapsed';

/** El aire del marco flotante de las pantallas (`lg:inset-y-2 lg:right-2` = 8px).
 *  Es lo que vale `sidebarWidth` cuando la sidebar está escondida: el mismo
 *  respiro de los otros tres lados. */
const MARGEN_MARCO = 8;
/**
 * Lo que el logo de la sidebar en tira se reserva en el borde izquierdo: las pantallas
 * que arrancan ahí (el encabezado del chat, el de Diseño, la esquina de Redes) le dejan
 * ese lugar (ADR-2026-09-16-el-lienzo-achica-la-sidebar-a-una-tira). Vive acá y no en el
 * shell porque el shell importa al chat, y al revés sería un ciclo.
 *
 * Es el borde DERECHO de la pastilla del logo (`EstiloMinimal`, "Mostrar el menú":
 * ADR-2026-10-06-el-logo-de-la-tira-va-en-pastilla) más un aire, sumado pieza por pieza
 * para que no vuelva a quedar corto: cuando el logo pasó de suelto a pastilla con chevron
 * esto siguió en 60 y el título "Agente" quedó debajo del botón
 * (ADR-2026-10-08-la-reserva-de-la-tira-mide-la-pastilla). Si la pastilla cambia de
 * medidas, cambia esto.
 */
const PASTILLA_LOGO_IZQUIERDA = 12; // `left-3`
const PASTILLA_LOGO_PADDING_IZQ = 8; // `pl-2`
const PASTILLA_LOGO_CHEVRON = 20; // `chevron_left` a `text-[20px]`
const PASTILLA_LOGO_GAP = 4; // `gap-1`
const PASTILLA_LOGO_ISOTIPO = 21; // `h-[20px] w-auto`, con viewBox 460×444
const PASTILLA_LOGO_PADDING_DER = 14; // `pr-3.5`
const AIRE_TRAS_LA_PASTILLA = 13;
export const ANCHO_LOGO_SIDEBAR_MINIMA =
  PASTILLA_LOGO_IZQUIERDA +
  PASTILLA_LOGO_PADDING_IZQ +
  PASTILLA_LOGO_CHEVRON +
  PASTILLA_LOGO_GAP +
  PASTILLA_LOGO_ISOTIPO +
  PASTILLA_LOGO_PADDING_DER +
  AIRE_TRAS_LA_PASTILLA; // = 92

interface SidebarContextType {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  sidebarWidth: number;
  mobileTitleOverride: string | null;
  setMobileTitleOverride: (title: string | null) => void;
  mobileBackOverride: (() => void) | null;
  setMobileBackOverride: (fn: (() => void) | null) => void;
  mobileHideBack: boolean;
  setMobileHideBack: (hide: boolean) => void;
  /** Si true, el header mobile pierde su `bg-white` + border y los
   *  botones (back / menu) toman fondo blanco circular semi-opaco.
   *  Pensado para pantallas como el preview de plantillas donde la
   *  imagen ocupa todo y el header debe flotar encima. */
  mobileHeaderTransparent: boolean;
  setMobileHeaderTransparent: (v: boolean) => void;
  /** La pantalla de abajo es oscura (el detalle de una creación): el botón de volver toma el
   *  vidrio oscuro en vez del círculo blanco
   *  (ADR-2026-10-28-la-botonera-del-telefono-toma-el-estilo-de-editar). */
  mobileHeaderOscuro: boolean;
  setMobileHeaderOscuro: (v: boolean) => void;
  /** Hay un visor a pantalla completa encima (el de una creación): el header se va con un
   *  fundido para que sus íconos no se vean a través del visor mientras entra
   *  (ADR-2026-10-28-el-header-se-va-con-el-visor). */
  mobileHeaderOculto: boolean;
  setMobileHeaderOculto: (v: boolean) => void;
  /** Reemplaza el título centrado del header mobile por un ReactNode
   *  arbitrario (ej. un toggle, chips). Si es null, se muestra el
   *  título habitual. */
  mobileHeaderCenter: ReactNode | null;
  setMobileHeaderCenter: (node: ReactNode | null) => void;
  /** Acción opcional a la derecha del header mobile (ej. un botón "Listo").
   *  Se renderiza DENTRO del header (z-50, contexto raíz) para escapar el
   *  stacking context del shell de contenido (z-30 + transform-gpu); un botón
   *  `fixed` puesto desde la página quedaría tapado por el header transparente.
   *  Si es null, no se muestra nada a la derecha. */
  mobileRightAction: ReactNode | null;
  setMobileRightAction: (node: ReactNode | null) => void;
  /** Lo que va pegado a la DERECHA del botón de volver del header mobile (el título de
   *  la pantalla, por ejemplo). No es `mobileTitleOverride`: ése lo setean pantallas
   *  viejas y el header ya no lo dibuja; revivirlo les cambiaría la cabecera a todas
   *  (ADR-2026-09-12-el-agente-en-mobile-usa-la-banda-del-volver). */
  mobileHeaderLeft: ReactNode | null;
  setMobileHeaderLeft: (node: ReactNode | null) => void;
  /** Oculta el pill de créditos en el header mobile transparente.
   *  Útil cuando la pantalla ya muestra otros CTAs o cuando el
   *  contador no aporta valor (ej. resultados de catálogo). */
  mobileHideCreditsPill: boolean;
  setMobileHideCreditsPill: (v: boolean) => void;
  /** Encabezado mobile COMPACTO: se van WhatsApp y la campana (con una transición) y la
   *  pastilla de créditos queda sola a la derecha. Lo pide la vista ampliada de una
   *  plantilla, que pone el selector Imagen/Video en el centro y los tres botones lo
   *  pisaban (ADR-2026-10-12-el-encabezado-compacto-en-la-vista-de-plantilla). */
  mobileHeaderCompacto: boolean;
  setMobileHeaderCompacto: (v: boolean) => void;
  /** Oculta la barra de navegación inferior mobile. Útil cuando una
   *  pantalla-tab entra en "modo tarea" y muestra su propia barra de acción
   *  fija abajo (ej. selección de productos con el CTA de generar). */
  mobileHideBottomNav: boolean;
  setMobileHideBottomNav: (v: boolean) => void;
  /** Esconde la sidebar de ESCRITORIO por completo (no la colapsa: no se
   *  dibuja) y pone `sidebarWidth` en 0, así el contenido toma la pantalla
   *  entera. Para pantallas que son una herramienta en sí misma y traen su
   *  propia salida —el Estudio de Video—. La prende la pantalla al montarse y
   *  la apaga al irse; el header mobile NO se toca (ahí la navegación de la
   *  app es lo único que hay). */
  hideSidebar: boolean;
  setHideSidebar: (v: boolean) => void;
  /**
   * Mientras está prendida, la sidebar de escritorio COLAPSADA se dibuja como una tira
   * con sólo el logo (ADR-2026-09-16-el-lienzo-achica-la-sidebar-a-una-tira). La
   * prende la pantalla que necesita el ancho —el Agente, mientras está montado— y la
   * apaga al irse, como el `hideSidebar` del Estudio de Video. No cambia la preferencia
   * del usuario (abierta / colapsada): sólo cómo se ve "colapsada" mientras dura, así el
   * resto de la app sigue con sus íconos. Quien la implementa es `EstiloMinimal`, que
   * es donde vive el estado de esa sidebar; acá sólo viaja la bandera.
   */
  sidebarMinima: boolean;
  setSidebarMinima: (v: boolean) => void;
  /**
   * Pedido —de una sola vez— de colapsar la sidebar. Lo hace el Agente cuando aparece
   * el lienzo: con `sidebarMinima` prendida, colapsada es la tira. Después el usuario la
   * abre cuando quiere y no se le vuelve a cerrar sola. El contador es la señal.
   */
  pedidoDeColapsar: number;
  pedirColapsarSidebar: () => void;
  /**
   * El pedido contrario, también de una sola vez: expandir la sidebar entera. Lo hace el
   * Catálogo al salir de su sesión, que la tuvo en la tira
   * (ADR-2026-10-14-salir-del-catalogo-expande-la-sidebar).
   */
  pedidoDeExpandir: number;
  pedirExpandirSidebar: () => void;
}

/**
 * Se exporta para que un shell alternativo pueda **re-proveer** el contexto con su
 * propia geometría (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell). El shell de `/inicio` tiene su sidebar en flujo y de
 * otro ancho que la de `DashboardLayout`; las pantallas que monta adentro anclan
 * paneles `fixed` a `sidebarWidth`, así que necesitan el número de ESE shell y no
 * el del provider de arriba. Sólo para eso: la app se sigue leyendo con `useSidebar`.
 */
export const SidebarContext = createContext<SidebarContextType>({
  isCollapsed: false,
  toggleCollapsed: () => {},
  sidebarWidth: 240,
  mobileTitleOverride: null,
  setMobileTitleOverride: () => {},
  mobileBackOverride: null,
  setMobileBackOverride: () => {},
  mobileHideBack: false,
  setMobileHideBack: () => {},
  mobileHeaderTransparent: false,
  setMobileHeaderTransparent: () => {},
  mobileHeaderOscuro: false,
  setMobileHeaderOscuro: () => {},
  mobileHeaderOculto: false,
  setMobileHeaderOculto: () => {},
  mobileHeaderCenter: null,
  setMobileHeaderCenter: () => {},
  mobileRightAction: null,
  setMobileRightAction: () => {},
  mobileHeaderLeft: null,
  setMobileHeaderLeft: () => {},
  mobileHideCreditsPill: false,
  setMobileHideCreditsPill: () => {},
  mobileHeaderCompacto: false,
  setMobileHeaderCompacto: () => {},
  mobileHideBottomNav: false,
  setMobileHideBottomNav: () => {},
  hideSidebar: false,
  setHideSidebar: () => {},
  sidebarMinima: false,
  setSidebarMinima: () => {},
  pedidoDeColapsar: 0,
  pedirColapsarSidebar: () => {},
  pedidoDeExpandir: 0,
  pedirExpandirSidebar: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === 'true'; } catch { return false; }
  });

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSED_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  // Listen for sidebar state changes via custom events (from Sidebar component)
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      setIsCollapsed(e.detail.isCollapsed);
      try { localStorage.setItem(COLLAPSED_KEY, String(e.detail.isCollapsed)); } catch {}
    };
    window.addEventListener('sidebar-toggle' as any, handler as EventListener);
    return () => window.removeEventListener('sidebar-toggle' as any, handler as EventListener);
  }, []);

  const [mobileTitleOverride, setMobileTitleOverride] = useState<string | null>(null);
  const [mobileBackOverride, setMobileBackOverride] = useState<(() => void) | null>(null);
  const [mobileHideBack, setMobileHideBack] = useState(false);
  const [mobileHeaderTransparent, setMobileHeaderTransparent] = useState(false);
  const [mobileHeaderOscuro, setMobileHeaderOscuro] = useState(false);
  const [mobileHeaderOculto, setMobileHeaderOculto] = useState(false);
  const [mobileHeaderCenter, setMobileHeaderCenter] = useState<ReactNode | null>(null);
  const [mobileRightAction, setMobileRightAction] = useState<ReactNode | null>(null);
  const [mobileHeaderLeft, setMobileHeaderLeft] = useState<ReactNode | null>(null);
  const [mobileHideCreditsPill, setMobileHideCreditsPill] = useState(false);
  const [mobileHeaderCompacto, setMobileHeaderCompacto] = useState(false);
  const [mobileHideBottomNav, setMobileHideBottomNav] = useState(false);
  const [hideSidebar, setHideSidebar] = useState(false);
  const [sidebarMinima, setSidebarMinima] = useState(false);
  const [pedidoDeColapsar, setPedidoDeColapsar] = useState(0);
  const pedirColapsarSidebar = useCallback(() => setPedidoDeColapsar((n) => n + 1), []);
  const [pedidoDeExpandir, setPedidoDeExpandir] = useState(0);
  const pedirExpandirSidebar = useCallback(() => setPedidoDeExpandir((n) => n + 1), []);

  // Ancho de la sidebar (al ras, sin gap flotante). El contenido arranca
  // pegado a este borde y se monta "encima" con un panel de esquina curva.
  //
  // Escondida NO vale 0 sino 8: este número es el borde izquierdo contra el que
  // se posicionan los marcos `fixed` de las pantallas, y el aire de ESE lado
  // sale de acá (el resto lo ponen ellas con `lg:inset-y-2 lg:right-2`). Con 0
  // la pantalla quedaba pegada al borde de la ventana y el marco se veía
  // cortado de un solo lado.
  const sidebarWidth = hideSidebar ? MARGEN_MARCO : isCollapsed ? 72 : 264;

  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleCollapsed, sidebarWidth, mobileTitleOverride, setMobileTitleOverride, mobileBackOverride, setMobileBackOverride, mobileHideBack, setMobileHideBack, mobileHeaderTransparent, setMobileHeaderTransparent, mobileHeaderOscuro, setMobileHeaderOscuro, mobileHeaderOculto, setMobileHeaderOculto, mobileHeaderCenter, setMobileHeaderCenter, mobileRightAction, setMobileRightAction, mobileHeaderLeft, setMobileHeaderLeft, mobileHideCreditsPill, setMobileHideCreditsPill, mobileHeaderCompacto, setMobileHeaderCompacto, mobileHideBottomNav, setMobileHideBottomNav, hideSidebar, setHideSidebar, sidebarMinima, setSidebarMinima, pedidoDeColapsar, pedirColapsarSidebar, pedidoDeExpandir, pedirExpandirSidebar }}>
      {children}
    </SidebarContext.Provider>
  );
};
